import React, { useRef } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import PropTypes from 'prop-types';
import {
    FormattedMessage,
    FormattedDate,
    defineMessages,
    useIntl,
} from 'react-intl';
import classNames from 'classnames';
import dayjs from 'dayjs';

import {
    LogoFull,
    PrimaryAnchor,
    getProvinceNameMessage,
    IconUser,
    LogoLetterMark,
    Colours,
    Sizes,
    StarRating,
    formatPhoneNumber,
    scrollIntoView,
    PageBehaviourButton,
    IconPlus,
    IconMinus,
    useSmoothCollapse,
} from '@ratehub/base-ui';

import { getApplicationUrlWithAgent } from '@ratehub/mtg-common';

import {
    LayoutRow,
    StickyBanner,
    MAX_WIDTH_BREAKPOINT,
} from '@ratehub/web-components';

import Layout from './Layout';


function MortgageAgentDetailsPage({ agentDetails, className }) {
    const intl = useIntl();
    const reviewContainerRef = useRef(null);

    const {
        agentID,
        image,
        firstName,
        lastName,
        phone,
        phoneExt,
        email,
        opportunityOwnerRegion,
        provinces,
        bio,
        averageRating,
        reviewCount,
        reviews,
        selectable,
        title,
        licenseNumber,
    } = agentDetails;

    const fullName = `${firstName} ${lastName}`;
    // convert averageRating from a 0-5 scale to a 0-100 scale
    const agentRating = convertToOneHundredRateScale(averageRating);
    const formattedPhoneNumber = !!phone && formatPhoneNumber(phone);
    const translatedAgentProvinceNameMessage = getProvinceNameMessage(opportunityOwnerRegion || provinces[0].id);
    const applicationURLWithAgentID = getApplicationUrlWithAgent(intl.locale, agentID, selectable) ?? '#';

    let sortedReviews = reviews.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate));

    const showMoreOrLessButton = reviewCount > FIRST_FEW_REVIEWS_COUNT;
    const [ isExpanded, toggleIsExpanded ] = useSmoothCollapse({
        containerRef: reviewContainerRef,
        controlledIsExpanded: !showMoreOrLessButton,
    });

    if (showMoreOrLessButton) {
        sortedReviews = sortedReviews.slice(0, isExpanded ? reviewCount : FIRST_FEW_REVIEWS_COUNT);
    }

    return (
        <Layout
            metaTitle={intl.formatMessage(MESSAGES.pageTitle, agentDetails)}
            metaDescription={intl.formatMessage(
                MESSAGES.metaDescription,
                agentDetails,
            )}
            hasStickyBanner={selectable}
            hideBackgroundImage
            className={className}
        >
            <LayoutRow
                includeContentPadding={false}
                className="rh-layout-default"
                style={{ gridRow: 1 }}
            >
                <div className="agent-details-section rh-display-flex rh-flex-wrap rh-mt-2 rh-mb-0_25 rh-width-100p">
                    <div className="primary-image-container rh-mx-auto rh-my-0">
                        <Choose>
                            <When condition={!!image}>
                                <img
                                    src={image}
                                    alt={`${fullName}`}
                                    className="primary-agent-image rh-border-radius-50p rh-mx-auto rh-my-0 rh-border-width-2px rh-border-style-solid rh-border-color-blueberry"
                                />
                            </When>
                            <Otherwise>
                                <IconUser
                                    className="primary-agent-image fallback-agent-image rh-border-radius-50p rh-mx-auto rh-my-0 rh-border-width-2px rh-border-style-solid rh-border-color-blueberry"
                                    fill={Colours.STONE_LIGHT}
                                    stroke={Colours.STONE}
                                    outlineWidth="0"
                                />
                            </Otherwise>
                        </Choose>
                    </div>
                    <div className="agent-contact-details rh-display-flex rh-align-items-center">
                        <p className="rh-text-m rh-display-flex rh-align-items-center rh-my-0">
                            <span className="rh-agent-logo rh-bg-blackberry rh-border-radius-50p rh-display-flex rh-justify-content-center rh-align-items-center">
                                <LogoLetterMark
                                    className="rh-agent-logo-lettermark"
                                    imageAlt="Ratehub"
                                    variant="light"
                                    width={15}
                                    height={15}
                                />
                            </span>
                            <span className="rh-ml-0_75 rh-mr-1" >
                                {'Ratehub'}
                            </span>
                            <span>
                                <FormattedMessage
                                    {...translatedAgentProvinceNameMessage}
                                />
                            </span>
                        </p>
                        <h1
                            className="rh-title-3xl rh-mt-1_5 rh-mb-0_25 rh-text-align-center"
                        >
                            {fullName}
                        </h1>
                        <div className="rh-display-flex rh-justify-content-center rh-mb-0_75">
                            <If condition={title}>
                                <p
                                    className="rh-text-s rh-my-0"
                                    // eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: title,
                                    }}
                                />
                            </If>
                            <If condition={title && licenseNumber}>
                                <p className="rh-text-s rh-my-0 rh-mx-0_5">
                                    {'|'}
                                </p>
                            </If>
                            <If condition={licenseNumber}>
                                <p className="rh-text-s rh-my-0">
                                    <FormattedMessage
                                        id="mtg.agent-details.licenseNumber"
                                        defaultMessage="ON Lic. {licenseNumber}"
                                        values={{
                                            licenseNumber: licenseNumber,
                                        }}
                                    />
                                </p>
                            </If>
                        </div>
                        <div className="rh-display-flex rh-align-items-center rh-mb-1_5">
                            <StarRating
                                ratingPercent={agentRating}
                            />
                            <a
                                href="#reviews"
                                onClick={() => {
                                    scrollIntoView(reviewContainerRef?.current);
                                }}
                                className="rh-ml-0_75 rh-mb-0 rh-fg-blackberry rh-title-3xs"
                            >
                                <FormattedMessage
                                    id="mtg.agent-details.review-count-link"
                                    defaultMessage={`{reviewCount, plural,
                                        one {{reviewCount, number} review}
                                        other {{reviewCount, number} reviews}
                                    }`}
                                    values={{
                                        reviewCount,
                                    }}
                                />
                            </a>
                        </div>
                        <If condition={phone}>
                            <p className="rh-my-0">
                                <FormattedMessage
                                    id="mtg.agent-details.phone-number"
                                    defaultMessage={`Phone: {phoneNumber} {hasExtension, select,
                                            true {ext. {extension}}
                                            other {}
                                        }
                                    `}
                                    values={{
                                        phoneNumber: formattedPhoneNumber,
                                        hasExtension: !!phoneExt,
                                        extension: phoneExt,
                                    }}
                                />
                            </p>
                        </If>
                        <p>
                            <FormattedMessage
                                id="mtg.agent-details.email"
                                defaultMessage={'Email: <highlightText>{email}</highlightText>'}
                                values={{
                                    email: email,
                                    highlightText: msg => (
                                        <a
                                            href={`mailto:${msg}`}
                                            className="rh-text-align-center rh-text-decoration-none rh-text-decoration-none rh-title-3xs rh-fg-blackberry rh-mb-1_5"
                                        >
                                            {msg}
                                        </a>
                                    ),
                                }}
                            />
                        </p>
                        <PrimaryAnchor
                            message={{
                                descriptor: MESSAGES.applyWithAgent,
                                values: { firstName },
                            }}
                            href={applicationURLWithAgentID}
                            shouldLowerCaseMessage={false}
                            size="small"
                            className={classNames('rh-max-height-3 apply-with-agent-button', { 'opacity-override': !selectable })}
                            disabled={!selectable}
                        />
                    </div>
                </div>
            </LayoutRow>

            <LayoutRow
                includeContentPadding={false}
                className="rh-layout-default"
                style={{ gridRow: 2 }}
            >
                <If condition={selectable}>
                    <StickyBanner>
                        <div className="sticky-mortgage-agent-banner-container rh-position-relative rh-display-flex rh-justify-content-center rh-py-0_5">
                            <LogoFull className="rh-width-auto banner-logo-full rh-position-absolute rh-left-0" />
                            <div className="sticky-agent-details rh-display-flex rh-flex-wrap rh-align-items-center rh-width-100p">
                                <div className="agent-image-name rh-display-flex">
                                    <Choose>
                                        <When condition={!!image}>
                                            <img
                                                src={image}
                                                alt={`${fullName}`}
                                                className="profile-image rh-mr-1 rh-border-radius-50p rh-border-width-1px rh-border-style-solid rh-border-color-blueberry"
                                            />
                                        </When>
                                        <Otherwise>
                                            <IconUser
                                                className="profile-image fallback-profile-image rh-mr-1 rh-border-radius-50p rh-border-width-1px rh-border-style-solid rh-border-color-blueberry"
                                                fill={Colours.STONE_LIGHT}
                                                stroke={Colours.STONE}
                                                outlineWidth="0"
                                            />
                                        </Otherwise>
                                    </Choose>
                                    <div>
                                        <p className="rh-title-s rh-m-0">
                                            {fullName}
                                        </p>
                                        <If condition={opportunityOwnerRegion}>
                                            <p className="rh-text-m rh-m-0">
                                                <FormattedMessage
                                                    {...translatedAgentProvinceNameMessage}
                                                />
                                            </p>
                                        </If>
                                    </div>
                                </div>
                                <PrimaryAnchor
                                    message={{
                                        descriptor: MESSAGES.applyWithAgent,
                                        values: { firstName },
                                    }}
                                    href={applicationURLWithAgentID}
                                    shouldLowerCaseMessage={false}
                                    size="small"
                                    className="rh-max-height-3 apply-with-agent-button"
                                />
                            </div>
                        </div>
                    </StickyBanner>
                </If>
            </LayoutRow>

            <LayoutRow
                includeContentPadding={false}
                className="rh-layout-default"
                style={{ gridRow: 3 }}
            >
                <If condition={bio}>
                    <div className="agent-details-bio rh-py-2 rh-px-1_5 rh-mt-3 rh-border-radius-5px rh-width-100p">
                        <h2 className="rh-title-s rh-mt-0 rh-mb-1">
                            <FormattedMessage
                                id="mtg.agent-details.about-agent"
                                defaultMessage="About {firstName}"
                                values={{ firstName }}
                            />
                        </h2>
                        <p
                            className="rh-text-s rh-text-align-justify"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: bio,
                            }}
                        />
                    </div>
                </If>
            </LayoutRow>

            <LayoutRow
                includeContentPadding={false}
                className="rh-layout-default"
                style={{ gridRow: 4 }}
            >
                <div
                    id="reviews"
                    className="rh-py-2 rh-px-1_5 rh-mt-3 rh-width-100p"
                    ref={reviewContainerRef}
                >
                    <div className="agent-reviews-summary-header rh-mb-1_5">
                        <h2 className="rh-title-s rh-m-0">
                            <FormattedMessage
                                id="mtg.agent-details.reviews-container-title"
                                defaultMessage={`{reviewCount, plural,
                                    one {Review}
                                    other {Reviews}
                                }`}
                                values={{
                                    reviewCount,
                                }}
                            />
                        </h2>
                        <div className="rh-display-flex rh-align-items-center rh-pt-1 rh-pb-1_5">
                            <StarRating
                                ratingPercent={agentRating}
                            />
                            <p className="rh-title-2xs rh-ml-0_75 rh-my-0">
                                <FormattedMessage
                                    id="mtg.agent-details.review-count"
                                    defaultMessage={`{reviewCount, plural,
                                        one {{averageRating, number} | {reviewCount, number} review}
                                        other {{averageRating, number} | {reviewCount, number} reviews}
                                    }`}
                                    values={{
                                        reviewCount,
                                        averageRating,
                                    }}
                                />
                            </p>
                        </div>
                    </div>
                    <Choose>
                        <When condition={sortedReviews.length > 0}>
                            <For
                                each="review"
                                of={sortedReviews}
                                index="index"
                            >
                                <div
                                    className="rh-mb-2"
                                    key={index}
                                >
                                    <div className="rh-display-flex rh-align-items-center">
                                        <StarRating
                                            starSize={Sizes.ICONS['2XS']}
                                            ratingPercent={convertToOneHundredRateScale(review.starRating)}
                                        />
                                        <p 
                                            className="rh-text-xs rh-ml-0_75" 
                                            suppressHydrationWarning // due to timezone differences, hydration could cause a mismatch if the client timezone differs from the server timezone
                                        >
                                            <FormattedDate
                                                value={dayjs(review.reviewDate)}
                                                year="numeric"
                                                month="short"
                                                day="2-digit"
                                            />
                                        </p>
                                    </div>
                                    <p
                                        className="rh-text-s rh-my-0"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: review.clientReview,
                                        }}
                                    />
                                </div>
                            </For>
                            <If condition={showMoreOrLessButton}>
                                <PageBehaviourButton
                                    onClick={toggleIsExpanded}
                                    iconComponent={isExpanded ? IconMinus : IconPlus}
                                    iconLocation="left"
                                    size="small"
                                >
                                    <FormattedMessage
                                        id="mtg.agent-details.reviews.show-more-or-less"
                                        defaultMessage="{isExpanded, select, true {show less} other {show more}}"
                                        values={{ isExpanded }}
                                    />
                                </PageBehaviourButton>
                            </If>
                        </When>
                        <Otherwise>
                            <p className="rh-my-2 rh-text-align-center">
                                <FormattedMessage
                                    id="mtg.agent-details.no-reviews"
                                    defaultMessage="{firstName} has no reviews yet."
                                    values={{
                                        firstName,
                                    }}
                                />
                            </p>
                        </Otherwise>
                    </Choose>
                </div>
            </LayoutRow>
        </Layout>
    );
}

MortgageAgentDetailsPage.propTypes = {
    agentDetails: PropTypes.object.isRequired,
    className: PropTypes.string,
};

MortgageAgentDetailsPage.defaultProps = {
    className: '',
};

const FIRST_FEW_REVIEWS_COUNT = 8;

function convertToOneHundredRateScale(rating = 0, initialScale = 5) {
    // convert rating from a 0-[initialScale] scale to a 0-100 scale
    return rating / initialScale * 100;
}

const StyledMortgageAgentDetailsPage = styled(MortgageAgentDetailsPage)`
    .agent-details-section {
        gap: ${Sizes.SPACING.TWO};
    }

    .agent-details-bio {
        background-color: ${rgba(Colours.BLUEBERRY_LIGHTEST, 0.4)};

        .rh-text-align-justify {
            text-align: justify;
        }
    }

    .primary-image-container {
        min-width: 16rem;
        min-height: 16rem;
        flex: 0 1 auto;

        .primary-agent-image {
            align-self: center;
            width: 16.5rem; // 275px is the width of the image in the designs, but reduced by about 10%
            object-fit: cover;
            aspect-ratio: 1;
            object-position: 50% 0%;

            &.fallback-agent-image {
                border: none;
                object-position: 50% 50%; // initial value
            }
        }
    }

    .agent-contact-details {
        flex-direction: column;
        flex: 2 1 auto;

        > .apply-with-agent-button {
            max-width: 28rem;

            &.opacity-override {
                // When a PrimaryAnchor is disabled an opacity of 0.5 is set using rh-opacity-0_5, this disregards any addition opacity classes
                // which is why this override class exists
                opacity: 0;
            }
        }
    }

    .rh-agent-logo {
        width: ${Sizes.SPACING.TWO};
        height: ${Sizes.SPACING.TWO};

        .rh-agent-logo-lettermark {
            margin-left: 2.5px;
        }
    }

    .sticky-mortgage-agent-banner-container {
        min-height: 5.125rem;

        > .banner-logo-full {
            top: 50%;
            transform: translate(0, -50%);

            // This is to match the logo used in the <Header /> component
            @media ${MAX_WIDTH_BREAKPOINT} {
                display: none;
            }
        }

        > .sticky-agent-details {
            max-width: 35.25rem;
            column-gap: ${Sizes.SPACING.ONE_AND_A_HALF};
            row-gap: ${Sizes.SPACING.ONE};

            .profile-image {
                width: 3.4375rem;
                height: 3.4375rem;

                object-fit: cover;
                object-position: 50% 0%;

                &.fallback-profile-image {
                    border: none;
                    object-position: 50% 50%; // initial value
                }
            }

            > .apply-with-agent-button {
                flex: 1 0 42%;
            }
        }
    }

    .agent-reviews-summary-header {
        border-bottom: 1px solid ${Colours.STONE_DARK};
    }
`;

const MESSAGES = defineMessages({
    pageTitle: {
        id: 'mtg.agent-details.page-title',
        defaultMessage: '{firstName} {lastName} | Ratehub.ca',
    },
    metaDescription: {
        id: 'mtg.agent-details.meta-description',
        defaultMessage: '{firstName} {lastName} is a Mortgage Agent with Ratehub.ca. Learn more about {firstName} {lastName} by reading customer reviews and exploring their profile.',
    },
    applyWithAgent: {
        id: 'mtg.agent-details.apply-with-agent',
        defaultMessage: 'apply with {firstName}',
    },
});

export default StyledMortgageAgentDetailsPage;
